import React, { useMemo } from "react";
import { motion } from "framer-motion";

const rand = (s) => { let x = Math.sin(s) * 10000; return x - Math.floor(x); };

// Neocortex laminar layers (real cytoarchitecture I–VI)
const LAYERS = [
  { r: "I", name: "Molecular", y0: 118, y1: 156 },
  { r: "II", name: "External granular", y0: 156, y1: 210 },
  { r: "III", name: "External pyramidal", y0: 210, y1: 288 },
  { r: "IV", name: "Internal granular", y0: 288, y1: 338 },
  { r: "V", name: "Internal pyramidal", y0: 338, y1: 420 },
  { r: "VI", name: "Multiform", y0: 420, y1: 492 },
];
const SLAB_X0 = 636, SLAB_X1 = 928;
const HX = 232, HY = 302;          // hippocampus centre
const CORE_X = 470, CORE_Y = 300;  // entorhinal relay core (bridge midpoint)

// A pyramidal neuron: triangular soma + apical dendrite rising with a tuft
function pyramidal(x, somaY, topY, key) {
  const w = 7;
  const soma = `M ${x - w} ${somaY} L ${x + w} ${somaY} L ${x} ${somaY - 12} Z`;
  const apical = `M ${x} ${somaY - 12} L ${x} ${topY}`;
  const tuft = `M ${x} ${topY} l -9 -10 M ${x} ${topY} l 9 -10 M ${x} ${topY} l 0 -12 M ${x} ${topY + 8} l -7 -8 M ${x} ${topY + 8} l 7 -8`;
  const basal = `M ${x - w} ${somaY} l -10 9 M ${x + w} ${somaY} l 10 9 M ${x} ${somaY} l 0 12`;
  return { soma, apical, tuft, basal, x, somaY, topY, key };
}

function build() {
  // Neocortex pyramidal cells in layer III and layer V
  const neurons = [];
  const xsIII = [668, 712, 756, 800, 844, 888];
  xsIII.forEach((x, i) => neurons.push(pyramidal(x, 262, 132, `p3_${i}`)));
  const xsV = [690, 740, 790, 840, 890];
  xsV.forEach((x, i) => neurons.push(pyramidal(x, 392, 224, `p5_${i}`)));

  // Hippocampus — nested CA arcs (seahorse loop)
  const arcs = [0, 1, 2].map((i) => {
    const r = 58 + i * 34;
    return `M ${HX - r} ${HY} A ${r} ${r * 0.92} 0 1 1 ${HX + r} ${HY - 4}`;
  });
  const spiral = `M ${HX - 150} ${HY + 40} C ${HX - 120} ${HY - 150}, ${HX + 150} ${HY - 130}, ${HX + 120} ${HY + 20} S ${HX - 40} ${HY + 150}, ${HX - 10} ${HY + 40}`;

  // Consolidation relay fibers hippocampus -> cortex (entorhinal input, layer IV), routed through the core
  const fibers = [0, 1, 2, 3, 4, 5].map((i) => {
    const sy = HY - 80 + i * 32;
    const dy = 260 + i * 24;
    const midY = CORE_Y + (i - 2.5) * 30;
    return { id: `relay${i}`, d: `M ${HX + 118} ${sy} Q ${CORE_X} ${midY}, ${SLAB_X0} ${dy}`, dur: 2.4 + rand(i) * 1.3 };
  });

  return { neurons, arcs, spiral, fibers };
}

export const NeuralBridge = ({ className = "", snapshot = null, live = false }) => {
  const { neurons, arcs, spiral, fibers } = useMemo(build, []);
  const cons = snapshot ? Math.max(0.15, snapshot.consolidation ?? 0.5) : 0.55;
  // learning intensity: surges harder + faster when actively absorbing
  const intensity = live ? 1 : 0.42;
  const sf = (1 / (0.6 + cons)) * (live ? 0.55 : 1);   // higher consolidation & live → faster relays
  const glow = snapshot ? 0.55 + cons * 0.45 : 1;        // brighter as it consolidates
  const surgeDur = (live ? 1.5 : 3.2).toFixed(2);        // master learning-surge cadence

  return (
    <div className={`relative overflow-hidden ${className}`} data-testid="neural-bridge">
      {/* cinematic ambient bloom that breathes with learning */}
      <motion.div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(60% 55% at 47% 50%, rgba(0,229,255,0.10), transparent 70%)" }}
        animate={{ opacity: live ? [0.5, 1, 0.5] : [0.25, 0.4, 0.25] }}
        transition={{ duration: live ? 1.6 : 4, repeat: Infinity, ease: "easeInOut" }}
      />

      {snapshot && (
        <div className="pointer-events-none absolute left-3 top-3 z-10 flex items-center gap-2 rounded-full border border-cyan-400/20 bg-black/50 px-3 py-1 backdrop-blur-sm" data-testid="brain-live-hud">
          <motion.span
            className="h-2 w-2 rounded-full"
            style={{ background: live ? "#ccff00" : "#00e5ff", boxShadow: `0 0 8px ${live ? "#ccff00" : "#00e5ff"}` }}
            animate={{ opacity: [0.4, 1, 0.4], scale: live ? [1, 1.5, 1] : [1, 1.2, 1] }}
            transition={{ duration: live ? 0.8 : 1.8, repeat: Infinity }}
          />
          <span className="font-tac text-[9px] tracking-[0.2em] text-white/75">
            {live ? "ABSORBING" : "CONSOLIDATED"} · {snapshot.episodes?.toLocaleString?.() ?? snapshot.episodes} OBS · {Math.round(cons * 100)}%
          </span>
        </div>
      )}

      <svg viewBox="0 0 1000 600" className="h-full w-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="hAura" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#ff7300" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#ff7300" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="coreGrad" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#eaffff" stopOpacity="0.95" />
            <stop offset="35%" stopColor="#00e5ff" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#0055ff" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="slabGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00e5ff" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#0055ff" stopOpacity="0.03" />
          </linearGradient>
          <linearGradient id="relayGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#ff7300" />
            <stop offset="50%" stopColor="#eaffff" />
            <stop offset="100%" stopColor="#00e5ff" />
          </linearGradient>
          <linearGradient id="sweepGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#ccff00" stopOpacity="0" />
            <stop offset="50%" stopColor="#ccff00" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#ccff00" stopOpacity="0" />
          </linearGradient>
          <filter id="glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="1.6" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="bigglow" x="-120%" y="-120%" width="340%" height="340%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
          <pattern id="microgrid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0 L0 0 0 40" fill="none" stroke="#ffffff" strokeOpacity="0.03" strokeWidth="1" />
          </pattern>
        </defs>

        <rect width="1000" height="600" fill="url(#microgrid)" />
        <circle cx={HX} cy={HY} r="185" fill="url(#hAura)" />

        {/* measurement ticks */}
        {[...Array(11)].map((_, i) => (
          <line key={i} x1={60 + i * 88} y1="560" x2={60 + i * 88} y2={i % 5 === 0 ? "548" : "554"} stroke="#ffffff" strokeOpacity="0.12" />
        ))}
        <line x1="60" y1="560" x2="940" y2="560" stroke="#ffffff" strokeOpacity="0.1" />

        {/* ===== NEOCORTEX laminar slab ===== */}
        <rect x={SLAB_X0} y="118" width={SLAB_X1 - SLAB_X0} height="374" rx="6" fill="url(#slabGrad)" stroke="#00e5ff" strokeOpacity="0.18" />
        {LAYERS.map((L) => (
          <g key={L.r}>
            <line x1={SLAB_X0} y1={L.y1} x2={SLAB_X1} y2={L.y1} stroke="#00e5ff" strokeOpacity="0.12" strokeDasharray="2 5" />
            <text x={SLAB_X1 + 10} y={(L.y0 + L.y1) / 2 + 3} fill="#00e5ff" fontSize="9" fontFamily="JetBrains Mono" opacity="0.6">{L.r}</text>
            <text x={SLAB_X1 + 26} y={(L.y0 + L.y1) / 2 + 3} fill="#ffffff" fontSize="7.5" fontFamily="JetBrains Mono" opacity="0.28">{L.name}</text>
          </g>
        ))}

        {/* consolidation sweep — a bright wave washing across the cortex each learning cycle */}
        <rect x={SLAB_X0} y="118" width="46" height="374" fill="url(#sweepGrad)" opacity={live ? 0.9 : 0.4}>
          <animate attributeName="x" values={`${SLAB_X0};${SLAB_X1 - 46};${SLAB_X0}`} dur={`${(live ? 3 : 6)}s`} repeatCount="indefinite" calcMode="easeInOut" />
        </rect>

        {/* canonical microcircuit input arrow */}
        <path d="M 620 313 L 636 313" stroke="#ccff00" strokeOpacity="0.6" strokeWidth="1.2" />

        {/* pyramidal neurons — somas flash in time with the learning surge */}
        {neurons.map((n, i) => (
          <g key={n.key} stroke="#00e5ff" strokeWidth="1" fill="none">
            <path id={`ap_${n.key}`} d={n.apical} strokeOpacity="0.45" />
            <path d={n.tuft} strokeOpacity="0.4" />
            <path d={n.basal} strokeOpacity="0.35" />
            <path d={n.soma} fill="#8ff4ff" stroke="none" opacity="0.9" filter="url(#glow)">
              <animate attributeName="opacity" values="0.55;1;0.55" dur={surgeDur} begin={`${(i * 0.12).toFixed(2)}s`} repeatCount="indefinite" />
            </path>
          </g>
        ))}
        {/* upward cortical signal pulses along apical dendrites */}
        {neurons.filter((_, i) => i % 2 === 0).map((n, i) => (
          <circle key={`sig${i}`} r={live ? 3 : 2.4} fill="#eaffff" filter="url(#glow)">
            <animateMotion dur={`${((2 + rand(i) * 1.6) * sf).toFixed(2)}s`} repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
              <mpath href={`#ap_${n.key}`} />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;0" dur={`${((2 + rand(i) * 1.6) * sf).toFixed(2)}s`} repeatCount="indefinite" />
          </circle>
        ))}

        {/* ===== HIPPOCAMPUS CA loop ===== */}
        <path id="hpath" d={spiral} fill="none" stroke="#ff7300" strokeOpacity={0.4 + glow * 0.2} strokeWidth="1.6" filter="url(#glow)" />
        {arcs.map((d, i) => (
          <path key={i} d={d} fill="none" stroke="#ff9a3d" strokeOpacity={0.3 - i * 0.07} strokeWidth="1.2" />
        ))}
        {[0, 1, 2].map((p) => (
          <circle key={p} r="3" fill="#ffd7a0" filter="url(#glow)">
            <animateMotion dur={`${(4 * sf).toFixed(2)}s`} begin={`${p * 1.3 * sf}s`} repeatCount="indefinite"><mpath href="#hpath" /></animateMotion>
          </circle>
        ))}
        {[...Array(live ? 16 : 10)].map((_, i) => {
          const a = (i / (live ? 16 : 10)) * Math.PI * 2; const r = 40 + rand(i) * 90;
          return <circle key={i} cx={HX + Math.cos(a) * r} cy={HY + Math.sin(a) * r * 0.9} r="2.4" fill="#ff9a3d">
            <animate attributeName="opacity" values="0.4;1;0.4" dur={`${(2 + rand(i) * 2) * sf}s`} repeatCount="indefinite" /></circle>;
        })}

        {/* ===== ENTORHINAL RELAY CORE — the living plasma bridge ===== */}
        <circle cx={CORE_X} cy={CORE_Y} r={live ? 60 : 42} fill="url(#coreGrad)" filter="url(#bigglow)">
          <animate attributeName="r" values={live ? "46;68;46" : "36;48;36"} dur={surgeDur} repeatCount="indefinite" calcMode="easeInOut" />
          <animate attributeName="opacity" values="0.55;1;0.55" dur={surgeDur} repeatCount="indefinite" />
        </circle>
        <circle cx={CORE_X} cy={CORE_Y} r="10" fill="#eaffff" filter="url(#glow)">
          <animate attributeName="r" values="7;13;7" dur={surgeDur} repeatCount="indefinite" />
        </circle>
        {/* expanding shockwave rings emitted every learning surge */}
        {[0, 1].map((k) => (
          <circle key={`ring${k}`} cx={CORE_X} cy={CORE_Y} fill="none" stroke="#00e5ff" strokeWidth="1.2">
            <animate attributeName="r" values="10;120" dur={`${(live ? 2 : 4)}s`} begin={`${k * (live ? 1 : 2)}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.8;0" dur={`${(live ? 2 : 4)}s`} begin={`${k * (live ? 1 : 2)}s`} repeatCount="indefinite" />
          </circle>
        ))}

        {/* ===== consolidation relay — dense fibres firing through the core ===== */}
        <g filter="url(#glow)">
          {fibers.map((f, i) => (
            <g key={f.id}>
              <path id={f.id} d={f.d} fill="none" stroke="url(#relayGrad)" strokeWidth={live ? 1.5 : 1.1} strokeOpacity={0.3 + glow * 0.25} strokeDasharray="1 6" />
              {/* primary travelling pulse */}
              <circle r={live ? 3.2 : 2.6} fill="#eaffff">
                <animateMotion dur={`${(f.dur * sf).toFixed(2)}s`} repeatCount="indefinite"><mpath href={`#${f.id}`} /></animateMotion>
                <animate attributeName="opacity" values="0;1;1;0" dur={`${(f.dur * sf).toFixed(2)}s`} repeatCount="indefinite" />
              </circle>
              {/* burst pulse only while actively absorbing */}
              {live && (
                <circle r="2.2" fill="#ccff00">
                  <animateMotion dur={`${(f.dur * sf).toFixed(2)}s`} begin={`${(f.dur * sf * 0.5).toFixed(2)}s`} repeatCount="indefinite"><mpath href={`#${f.id}`} /></animateMotion>
                  <animate attributeName="opacity" values="0;1;0" dur={`${(f.dur * sf).toFixed(2)}s`} begin={`${(f.dur * sf * 0.5).toFixed(2)}s`} repeatCount="indefinite" />
                </circle>
              )}
            </g>
          ))}
        </g>

        {/* labels */}
        <text x={HX} y="512" textAnchor="middle" fill="#ff7300" fontSize="14" fontFamily="Syncopate" letterSpacing="4" opacity="0.9">HIPPOCAMPUS</text>
        <text x="782" y="512" textAnchor="middle" fill="#00e5ff" fontSize="14" fontFamily="Syncopate" letterSpacing="4" opacity="0.9">NEOCORTEX</text>
        <text x={CORE_X} y="250" textAnchor="middle" fill="#ffffff" fontSize="8.5" fontFamily="JetBrains Mono" letterSpacing="2" opacity="0.45">
          ENTORHINAL RELAY · CONSOLIDATION →
        </text>
      </svg>

      {/* floating dust motes */}
      {[...Array(live ? 14 : 8)].map((_, i) => (
        <motion.span key={i} className="pointer-events-none absolute h-0.5 w-0.5 rounded-full"
          style={{ left: `${rand(i) * 100}%`, top: `${rand(i + 3) * 100}%`, background: i % 2 ? "#00e5ff" : "#ff7300" }}
          animate={{ opacity: [0.1, 0.6, 0.1], y: [0, -6, 0] }} transition={{ duration: 3 + rand(i) * 4, repeat: Infinity, delay: rand(i) * 3 }} />
      ))}

      {/* cinematic vignette */}
      <div className="pointer-events-none absolute inset-0" style={{ boxShadow: "inset 0 0 120px rgba(0,0,0,0.7)" }} />
    </div>
  );
};
